import { Controller } from "@hotwired/stimulus"
import { post, get } from "@rails/request.js"

// Connects to data-controller="shortcut-multiple-select"
export default class extends Controller {
  static targets = ["face"]
  static values = { 
    bulkCreateUrl: String,
    bulkFormUrl: String,
    selectedItems: { type: Number, default: 0 },
    mode: { type: String, default: "direct" } // "direct" o "form"
  }
  
  connect() {
    this.#updateSelectedItemsValue()
    this.shiftPressed = false
  
    this._handleKeyDown = this.#handleKeyDown.bind(this)
    this._handleKeyUp   = this.#handleKeyUp.bind(this)
    this._handleFrameRender = this.#handleFrameRender.bind(this)
  
    document.addEventListener('keydown', this._handleKeyDown)
    document.addEventListener('keyup', this._handleKeyUp)
    document.addEventListener('turbo:frame-render', this._handleFrameRender)
  }
  
  disconnect() {
    document.removeEventListener('keydown', this._handleKeyDown)
    document.removeEventListener('keyup', this._handleKeyUp)
    document.removeEventListener('turbo:frame-render', this._handleFrameRender)
  
    console.log("Shortcut Multiple Select controller disconnected")
  }

  toggle(event) {
    if (!event.shiftKey) return

    event.preventDefault()
    event.stopPropagation()

    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')
    const link = event.currentTarget
    const faceDiv = link.querySelector('[data-shortcut-multiple-select-target="face"]')
    
    if (checkbox) {
      checkbox.checked = !checkbox.checked
      this.#updateFaceSelection(faceDiv, checkbox.checked)
      this.#updateSelectedItemsValue()
    }
  }

  selectedItemsValueChanged() {
    this.element.classList.toggle("visible", this.selectedItemsValue !== 0)
  }

  #handleKeyDown(event) {
    if (event.key === 'Shift') {
      this.shiftPressed = true
    }
  }

  #handleKeyUp(event) {
    if (!this.element.isConnected) return
    
    if (event.key === 'Shift' && this.shiftPressed) {
      this.shiftPressed = false
      this.#processBulkSelection()
    }
  }

  async #processBulkSelection() {
    const selectedCheckboxes = [...this.#checkboxes].filter(checkbox => checkbox.checked)
    
    if (selectedCheckboxes.length === 0) return

    const selections = {}
    selectedCheckboxes.forEach(checkbox => {
      const value = checkbox.value
      const [toothId, face] = value.split('-')
      
      if (!selections[toothId]) {
        selections[toothId] = []
      }
      selections[toothId].push(face)
    })

    if (this.modeValue === "direct") {
      await this.#createDirectly(selections)
    } else if (this.modeValue === "form") {
      await this.#openFormWithSelections(selections)
    }
  }

  // creación directa (JSON) — odontograma inicial u otros flujos con mode "direct"
  async #createDirectly(selections) {
    const data = {
      selections: Object.keys(selections).map(toothId => ({
        tooth_id: toothId,
        faces: selections[toothId]
      }))
    }

    if (!this.bulkCreateUrlValue) return

    try {
      const response = await post(this.bulkCreateUrlValue, {
        body: JSON.stringify(data),
        contentType: "application/json",
        responseKind: "turbo-stream"
      })

      if (response.ok) {
        this.clearSelections()
      }
    } catch (error) {
      console.error('Error al crear tratamientos múltiples:', error)
    }
  }

  // abrir form multiple
  async #openFormWithSelections(selections) {
    if (!this.bulkFormUrlValue) {
      console.error("no se encontró bulkFormUrl")
      return
    }

    const selectionsParam = encodeURIComponent(JSON.stringify(selections))
    const separator = this.bulkFormUrlValue.includes('?') ? '&' : '?'
    const formUrl = `${this.bulkFormUrlValue}${separator}selections=${selectionsParam}`

    try {
      const response = await get(formUrl, {
        responseKind: "turbo-stream"
      })

      if (response.ok) {
        console.log("formulario multiple abierto")
      }
    } catch (error) {
      console.error("error en abrir formulario multiple:", error)
    }
  }

  #updateSelectedItemsValue() {
    this.selectedItemsValue = [...this.#checkboxes].filter(checkbox => checkbox.checked).length
  }

  #updateFaceSelection(faceDiv, isSelected) {
    if (!faceDiv) return
    
    if (isSelected) {
      faceDiv.classList.add('face-selected')
      if (!faceDiv.dataset.originalBackgroundColor) {
        faceDiv.dataset.originalBackgroundColor = faceDiv.style.backgroundColor || window.getComputedStyle(faceDiv).backgroundColor
      }
      faceDiv.style.boxShadow = 'inset 0 0 0 1000px rgba(59, 130, 246, 0.4)'
    } else {
      faceDiv.classList.remove('face-selected')
      faceDiv.style.boxShadow = ''
    }
  }
      
  get #checkboxes() {
    return this.element.querySelectorAll('input[type="checkbox"][name="selected_faces[]"]')
  }

  // limpiar caras
  clearSelections() {
    const selectedCheckboxes = [...this.#checkboxes].filter(checkbox => checkbox.checked)
    selectedCheckboxes.forEach(checkbox => {
      checkbox.checked = false
      const link = checkbox.closest('a')
      if (link) {
        const faceDiv = link.querySelector('[data-shortcut-multiple-select-target="face"]')
        this.#updateFaceSelection(faceDiv, false)
      }
    })
    this.#updateSelectedItemsValue()
  }

  handleFormSubmit(event) {
    const { success } = event.detail
    if (success) {
      this.clearSelections()
    }
  }

  #handleFrameRender(event) {
    const frame = event.target
    if (frame.id !== 'selected-form-content') return
    const isBulkForm = frame.innerHTML.includes('Tratamiento Múltiple de Coronas')
    
    if (!isBulkForm && this.selectedItemsValue > 0) {
      this.clearSelections()
    }
  }
}
